import { getDbSafe, getAuthSafe } from './firebaseConfig';
import {
  collection, doc, addDoc, setDoc, getDoc, getDocs, updateDoc, deleteDoc,
  query, where, orderBy, limit, serverTimestamp, onSnapshot, runTransaction,
  increment, Timestamp, writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getStorageSafe } from './firebaseConfig';
import {
  BusinessRegistration, BusinessProfile, BusinessFeedPost, BusinessStoreItem,
  PurchaseCheck, PendingBusinessEdit, BusinessOwnerState,
  STORE_ITEM_CAP, BUSINESS_COOLDOWN_DAYS, PORTFOLIO_IMAGE_COUNT,
  PITCH_MAX_LENGTH, BUSINESS_NAME_MAX_LENGTH, BUSINESS_DESCRIPTION_MAX_LENGTH,
  FEED_POST_MAX_LENGTH, BUSINESS_NOTIFICATION_TYPES, MAX_BUSINESS_PROFILES,
  normalizeShopLink, isValidShopLink, appendTrackingCode, toMillis, parseCustomerBase,
} from '../types/business';

/**
 * BusinessService — every client-side read and write for Business Profiles.
 *
 * Every limit checked here is ALSO enforced in firestore.rules. These checks
 * exist to produce a usable error message; the rules are the actual control.
 * Same division of labour as BanAppealService and GovernanceService.
 */

// ── Rate limiting ────────────────────────────────────────────────────────────
// Same shape as api.ts's _checkRateLimit: an in-memory speed bump, not security.
// The things that genuinely must hold — the store cap, the registration
// cooldown, the purchase-notification limit — are enforced in a transaction, a
// security rule, or a Cloud Function trigger respectively.
const _rateLimitMap: Map<string, Record<string, number>> = new Map();

function _checkRateLimit(uid: string, action: string, cooldownMs: number): void {
  const now = Date.now();
  const userLimits = _rateLimitMap.get(uid) || {};
  const last = userLimits[action] || 0;
  if (now - last < cooldownMs) {
    const remaining = Math.ceil((cooldownMs - (now - last)) / 1000);
    throw new Error(`Please wait ${remaining}s before doing that again.`);
  }
  userLimits[action] = now;
  _rateLimitMap.set(uid, userLimits);
}

type Result<T = void> = { success: boolean; error?: string; data?: T };

const COOLDOWN_MS = BUSINESS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

/** Short, unambiguous tracking code. No 0/O/1/I — these get read aloud and retyped. */
function generateTrackingCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(8);
  (globalThis.crypto || (window as any).crypto).getRandomValues(bytes);
  return Array.from(bytes, b => alphabet[b % alphabet.length]).join('');
}

function mapDoc<T>(snap: any): T {
  return { id: snap.id, ...snap.data() } as T;
}

export const BusinessService = {

  // ───────────────────────────────────────────────────────────────────────────
  // §2 REGISTRATION
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * This owner's two limits: the application cooldown and how many profiles they
   * already own.
   *
   * Reads business_owner_state/{uid} — one document per owner, id derivable from
   * the caller, exactly like ban_appeals. That shape is what lets the security
   * rules enforce the same two limits; a rule cannot run a query.
   */
  async getOwnerState(ownerId: string): Promise<BusinessOwnerState | null> {
    try {
      const db = await getDbSafe();
      const snap = await getDoc(doc(db, 'business_owner_state', ownerId));
      // Spread first, then ownerId: the document carries its own ownerId, but the
      // uid we were asked about is the authoritative one, and a state document
      // written with a stale or missing ownerId should not win.
      return snap.exists() ? ({ ...(snap.data() as BusinessOwnerState), ownerId }) : null;
    } catch (e) {
      console.warn('[BusinessService] getOwnerState failed:', e);
      return null;
    }
  },

  /**
   * How many of this owner's three business slots are spoken for, and by what.
   *
   * Counts live profiles plus registrations that have not yet become one and
   * have not been declined — an approved application someone has not paid for is
   * still a business they are in the middle of starting, and pretending
   * otherwise would let them queue up a fourth.
   *
   * This is the client-side view, for enabling buttons and writing honest error
   * messages. The authoritative cap is activeProfileCount in the rules, which
   * only the triggers can write.
   */
  async getSlotUsage(ownerId: string): Promise<{ used: number; profiles: number; inFlight: number; remaining: number }> {
    const [profiles, registrations] = await Promise.all([
      BusinessService.getMyProfiles(ownerId),
      BusinessService.getMyRegistrations(ownerId),
    ]);
    const built = new Set(profiles.map(p => p.registrationId));
    const inFlight = registrations.filter(r =>
      r.status !== 'denied'
      && !built.has(r.id)
      && !(r as any).profileDeletedAt,
    ).length;
    const used = profiles.length + inFlight;
    return {
      used,
      profiles: profiles.length,
      inFlight,
      remaining: Math.max(0, MAX_BUSINESS_PROFILES - used),
    };
  },

  /** When this owner may register again, or null if right now. */
  async getCooldown(ownerId: string): Promise<Date | null> {
    const state = await BusinessService.getOwnerState(ownerId);
    const ms = toMillis(state?.cooldownExpiresAt);
    return ms && ms > Date.now() ? new Date(ms) : null;
  },

  /**
   * Submit a pitch. This is NOT the profile — approval plus payment unlocks a
   * separate builder step, and nothing here is copied into the live profile.
   */
  async submitRegistration(
    ownerId: string,
    input: {
      businessName: string;
      pitchText: string;
      shopLink: string;
      socialLink?: string;
      /** Raw comma-separated text as typed; parsed and bounded here. */
      customerBase?: string;
    },
  ): Promise<Result<{ registrationId: string }>> {
    const businessName = (input.businessName || '').trim();
    if (businessName.length < 2) {
      return { success: false, error: 'Tell us what your business is called.' };
    }
    if (businessName.length > BUSINESS_NAME_MAX_LENGTH) {
      return { success: false, error: `Keep the name under ${BUSINESS_NAME_MAX_LENGTH} characters.` };
    }
    const pitch = (input.pitchText || '').trim();
    if (pitch.length < 30) {
      return { success: false, error: 'Tell us a bit more about your business first (at least 30 characters).' };
    }
    if (pitch.length > PITCH_MAX_LENGTH) {
      return { success: false, error: `Keep the pitch under ${PITCH_MAX_LENGTH} characters.` };
    }
    if (!isValidShopLink(input.shopLink)) {
      return { success: false, error: 'Add a working link to your shop or site.' };
    }
    // Optional, so an empty field is fine — but a filled-in one that is not a
    // link is a typo worth catching here rather than handing a moderator a
    // string that does nothing when they tap it.
    const social = (input.socialLink || '').trim();
    if (social && !isValidShopLink(social)) {
      return { success: false, error: 'That social media link does not look right. Leave it blank if you would rather.' };
    }

    try {
      const db = await getDbSafe();
      const auth = await getAuthSafe();
      if (!auth.currentUser || auth.currentUser.uid !== ownerId) {
        return { success: false, error: 'Not signed in.' };
      }

      const waitUntil = await BusinessService.getCooldown(ownerId);
      if (waitUntil) {
        return { success: false, error: `You can apply again after ${waitUntil.toLocaleDateString()}.` };
      }

      // One application in the queue at a time. Client-side only: a rule cannot
      // query for "does this owner already have a pending doc", and the practical
      // harm of a duplicate is a reviewer seeing the same pitch twice rather than
      // anything a rule needs to stop. The cooldown, which DOES need to hold, is
      // in the rules.
      const existing = await BusinessService.getMyRegistrations(ownerId);
      if (existing.some(r => r.status === 'pending')) {
        return { success: false, error: 'You already have an application being reviewed.' };
      }

      // The cap counts profiles AND applications still in flight. Counting only
      // built profiles would let someone file three applications before any of
      // them lands and end up with four businesses.
      const slots = await BusinessService.getSlotUsage(ownerId);
      if (slots.used >= MAX_BUSINESS_PROFILES) {
        return {
          success: false,
          error: `You can run ${MAX_BUSINESS_PROFILES} businesses at a time. Delete one to apply for another.`,
        };
      }

      _checkRateLimit(ownerId, 'submitRegistration', 10000);

      // Denormalized so the review queue reads one collection instead of fanning
      // out to /users for every row.
      let ownerName = '';
      let ownerUsername = '';
      try {
        const userSnap = await getDoc(doc(db, 'users', ownerId));
        if (userSnap.exists()) {
          ownerName = userSnap.data().name || '';
          ownerUsername = userSnap.data().username || '';
        }
      } catch { /* the queue can live without a display name */ }

      const created = await addDoc(collection(db, 'business_registrations'), {
        ownerId,
        ownerName,
        ownerUsername,
        businessName,
        pitchText: pitch,
        customerBase: parseCustomerBase(input.customerBase || ''),
        shopLink: normalizeShopLink(input.shopLink),
        socialLink: social ? normalizeShopLink(social) : null,
        status: 'pending',
        submittedAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: null,
        denialReason: null,
        deniedAt: null,
        cooldownExpiresAt: null,
        paidAt: null,
        stripeSessionId: null,
        businessProfileId: null,
      });

      return { success: true, data: { registrationId: created.id } };
    } catch (e: any) {
      console.error('[BusinessService] submitRegistration failed:', e);
      return { success: false, error: e?.message?.startsWith('Please wait')
        ? e.message
        : 'Could not submit your application right now.' };
    }
  },

  /** Every registration this owner has ever filed, newest first. */
  async getMyRegistrations(ownerId: string): Promise<BusinessRegistration[]> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'business_registrations'),
        where('ownerId', '==', ownerId),
      ));
      return snap.docs
        .map(d => mapDoc<BusinessRegistration>(d))
        .sort((a, b) => (toMillis(b.submittedAt) || 0) - (toMillis(a.submittedAt) || 0));
    } catch (e) {
      console.error('[BusinessService] getMyRegistrations failed:', e);
      return [];
    }
  },

  /**
   * Live view of one registration.
   *
   * This is what closes the Stripe loop: the client keeps this open across the
   * hop out to Checkout, and `paidAt` appearing is the signal that the webhook
   * landed. No redirect URL to get wrong, and it works whenever the user comes
   * back rather than only on a successful return.
   */
  watchRegistration(
    registrationId: string,
    onChange: (registration: BusinessRegistration | null) => void,
  ): () => void {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    (async () => {
      try {
        const db = await getDbSafe();
        if (cancelled) return;
        unsub = onSnapshot(
          doc(db, 'business_registrations', registrationId),
          snap => onChange(snap.exists() ? mapDoc<BusinessRegistration>(snap) : null),
          err => console.warn('[BusinessService] watchRegistration error:', err),
        );
      } catch (e) {
        console.warn('[BusinessService] watchRegistration failed to attach:', e);
      }
    })();
    return () => { cancelled = true; unsub?.(); };
  },

  // ── Admin review ───────────────────────────────────────────────────────────

  /** The review queue. Admin-only by rule; this just shapes the read. */
  async fetchRegistrations(status: 'pending' | 'approved' | 'denied' = 'pending'): Promise<BusinessRegistration[]> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'business_registrations'),
        where('status', '==', status),
        limit(200),
      ));
      return snap.docs
        .map(d => mapDoc<BusinessRegistration>(d))
        .sort((a, b) => (toMillis(a.submittedAt) || 0) - (toMillis(b.submittedAt) || 0));
    } catch (e) {
      console.error('[BusinessService] fetchRegistrations failed:', e);
      return [];
    }
  },

  /**
   * Greenlight an application.
   *
   * Approval never expires — there is no deadline on paying — so the Pay CTA
   * this unlocks stays reachable from My Business indefinitely.
   */
  async approveRegistration(registrationId: string, reviewerId: string): Promise<Result> {
    try {
      const db = await getDbSafe();
      const regRef = doc(db, 'business_registrations', registrationId);
      const snap = await getDoc(regRef);
      if (!snap.exists()) return { success: false, error: 'That application no longer exists.' };
      const reg = mapDoc<BusinessRegistration>(snap);
      if (reg.status !== 'pending') return { success: false, error: 'That application was already reviewed.' };

      await updateDoc(regRef, {
        status: 'approved',
        reviewedAt: serverTimestamp(),
        reviewedBy: reviewerId,
        denialReason: null,
      });

      // An approval clears any cooldown left over from an earlier denial —
      // otherwise a second application approved inside the window would leave a
      // stale block sitting on the account.
      try {
        await updateDoc(doc(db, 'business_owner_state', reg.ownerId), {
          cooldownExpiresAt: null,
          reason: null,
        });
      } catch { /* no state document on file, so nothing to clear */ }

      await BusinessService._notify(
        reg.ownerId,
        BUSINESS_NOTIFICATION_TYPES.APPROVED,
        'Your business was approved',
        'You are greenlit. Head to My Business to pay the one-time fee and build your profile.',
        { registrationId },
      );

      await BusinessService._audit(reviewerId, reg.ownerId, 'business_registration_approved',
        'Business registration approved', { registrationId, shopLink: reg.shopLink });

      return { success: true };
    } catch (e: any) {
      console.error('[BusinessService] approveRegistration failed:', e);
      return { success: false, error: 'Could not approve that application.' };
    }
  },

  /**
   * Deny an application and start the cooldown.
   *
   * The cooldown is written to its own collection, by the admin, in the same
   * flow — not to a field the applicant can reach. cooldownExpiresAt is also
   * mirrored onto the registration so My Business can show a countdown without a
   * second read, but the rule reads the cooldown document, which is the copy
   * that matters.
   */
  async denyRegistration(registrationId: string, reviewerId: string, reason: string): Promise<Result> {
    const denialReason = (reason || '').trim();
    if (!denialReason) return { success: false, error: 'Give a reason. The applicant sees it.' };

    try {
      const db = await getDbSafe();
      const regRef = doc(db, 'business_registrations', registrationId);
      const snap = await getDoc(regRef);
      if (!snap.exists()) return { success: false, error: 'That application no longer exists.' };
      const reg = mapDoc<BusinessRegistration>(snap);
      if (reg.status !== 'pending') return { success: false, error: 'That application was already reviewed.' };

      const expiresAt = Timestamp.fromMillis(Date.now() + COOLDOWN_MS);

      const batch = writeBatch(db);
      batch.update(regRef, {
        status: 'denied',
        reviewedAt: serverTimestamp(),
        reviewedBy: reviewerId,
        denialReason,
        deniedAt: serverTimestamp(),
        cooldownExpiresAt: expiresAt,
      });
      // merge: this document also carries activeProfileCount, which the
      // triggers own. A plain set would wipe it and hand the owner a free slot.
      batch.set(doc(db, 'business_owner_state', reg.ownerId), {
        ownerId: reg.ownerId,
        cooldownExpiresAt: expiresAt,
        reason: 'denied',
        updatedAt: serverTimestamp(),
      }, { merge: true });
      await batch.commit();

      await BusinessService._notify(
        reg.ownerId,
        BUSINESS_NOTIFICATION_TYPES.DENIED,
        'Your business application was declined',
        denialReason,
        { registrationId },
      );

      await BusinessService._audit(reviewerId, reg.ownerId, 'business_registration_denied',
        denialReason, { registrationId, cooldownDays: BUSINESS_COOLDOWN_DAYS });

      return { success: true };
    } catch (e: any) {
      console.error('[BusinessService] denyRegistration failed:', e);
      return { success: false, error: 'Could not deny that application.' };
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // §3 PAYMENT
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Ask the backend for a Stripe Checkout URL. The caller opens it externally
   * and watches the registration doc for `paidAt` — see watchRegistration.
   */
  async createCheckoutSession(registrationId: string): Promise<Result<{ url: string }>> {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const fn = httpsCallable(getFunctions(getApp()), 'createBusinessCheckoutSession');
      const res = await fn({ registrationId });
      const url = (res.data as any)?.url;
      if (!url) return { success: false, error: 'Payment is not available right now.' };
      return { success: true, data: { url } };
    } catch (e: any) {
      console.error('[BusinessService] createCheckoutSession failed:', e);
      // A callable surfaces its HttpsError message here, which is written for
      // the user ("This application has already been paid for", etc.).
      return { success: false, error: e?.message || 'Could not start checkout.' };
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // §4 PROFILE
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Build the live profile. Only reachable once the registration is approved AND
   * paid and has no profile yet — the rule checks the same three things.
   */
  async createProfile(
    ownerId: string,
    registrationId: string,
    input: { businessName: string; description: string; shopLink: string; portfolioImages: string[] },
  ): Promise<Result<{ profileId: string }>> {
    const businessName = (input.businessName || '').trim();
    if (businessName.length < 2) return { success: false, error: 'Give your business a name.' };
    if (businessName.length > BUSINESS_NAME_MAX_LENGTH) {
      return { success: false, error: `Keep the name under ${BUSINESS_NAME_MAX_LENGTH} characters.` };
    }
    const description = (input.description || '').trim();
    if (description.length > BUSINESS_DESCRIPTION_MAX_LENGTH) {
      return { success: false, error: `Keep the description under ${BUSINESS_DESCRIPTION_MAX_LENGTH} characters.` };
    }
    if (!isValidShopLink(input.shopLink)) return { success: false, error: 'Add a working link to your shop.' };
    const images = (input.portfolioImages || []).filter(Boolean);
    if (images.length !== PORTFOLIO_IMAGE_COUNT) {
      return { success: false, error: `Add all ${PORTFOLIO_IMAGE_COUNT} portfolio images.` };
    }

    try {
      const db = await getDbSafe();

      // Checked before the transaction so the message names the real problem.
      // The rule checks the same cap against a counter the client cannot write.
      const owned = await BusinessService.getMyProfiles(ownerId);
      if (owned.length >= MAX_BUSINESS_PROFILES) {
        return {
          success: false,
          error: `You already run ${MAX_BUSINESS_PROFILES} businesses. Delete one to build another.`,
        };
      }

      const regRef = doc(db, 'business_registrations', registrationId);
      const profileRef = doc(collection(db, 'business_profiles'));

      // One transaction so a profile can never exist without its registration
      // pointing at it, and a double-submit cannot mint two profiles from one
      // payment.
      await runTransaction(db, async (tx) => {
        const regSnap = await tx.get(regRef);
        if (!regSnap.exists()) throw new Error('That application no longer exists.');
        const reg = regSnap.data() as BusinessRegistration;
        if (reg.ownerId !== ownerId) throw new Error('That application is not yours.');
        if (reg.status !== 'approved') throw new Error('That application has not been approved.');
        if (!reg.paidAt) throw new Error('Payment has not cleared yet.');
        if (reg.businessProfileId) throw new Error('You already built this profile.');

        tx.set(profileRef, {
          ownerId,
          registrationId,
          businessName,
          businessNameLower: businessName.toLowerCase(),
          description: description || null,
          shopLink: normalizeShopLink(input.shopLink),
          portfolioImages: images,
          status: 'active',
          suspendedAt: null,
          suspendedBy: null,
          suspendReason: null,
          showOnPersonalProfile: true,
          pendingEdit: null,
          hasPendingEdit: false,
          storeItemCount: 0,
          viewCount: 0,
          storeTapCount: 0,
          confirmedPurchaseCount: 0,
          createdAt: serverTimestamp(),
          lastEditedAt: serverTimestamp(),
        });
        tx.update(regRef, { businessProfileId: profileRef.id });
      });

      return { success: true, data: { profileId: profileRef.id } };
    } catch (e: any) {
      console.error('[BusinessService] createProfile failed:', e);
      return { success: false, error: e?.message || 'Could not create your business profile.' };
    }
  },

  async getProfile(profileId: string): Promise<BusinessProfile | null> {
    try {
      const db = await getDbSafe();
      const snap = await getDoc(doc(db, 'business_profiles', profileId));
      return snap.exists() ? mapDoc<BusinessProfile>(snap) : null;
    } catch (e) {
      console.error('[BusinessService] getProfile failed:', e);
      return null;
    }
  },

  /**
   * This owner's profiles, suspended ones included.
   *
   * A suspended profile is hidden from Explore, search and the public link, but
   * it stays visible here labelled as suspended — a profile that simply vanished
   * would read as data loss rather than as a moderation decision.
   */
  async getMyProfiles(ownerId: string): Promise<BusinessProfile[]> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'business_profiles'),
        where('ownerId', '==', ownerId),
      ));
      return snap.docs
        .map(d => mapDoc<BusinessProfile>(d))
        .sort((a, b) => (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0));
    } catch (e) {
      console.error('[BusinessService] getMyProfiles failed:', e);
      return [];
    }
  },

  /**
   * Profiles to link from someone's personal profile: active, and toggled on.
   * The toggle is per profile, so an owner with three businesses can surface one.
   */
  async getLinkedProfiles(ownerId: string): Promise<BusinessProfile[]> {
    const all = await BusinessService.getMyProfiles(ownerId);
    return all.filter(p => p.status === 'active' && p.showOnPersonalProfile !== false);
  },

  /**
   * Freely-editable fields. Description, portfolio and the personal-profile
   * toggle change nothing an admin approved, so they go straight through.
   * businessName and shopLink do NOT come through here — see requestNameOrLinkEdit.
   */
  async updateProfile(
    profileId: string,
    updates: { description?: string; portfolioImages?: string[]; showOnPersonalProfile?: boolean },
  ): Promise<Result> {
    try {
      const db = await getDbSafe();
      const patch: any = { lastEditedAt: serverTimestamp() };

      if (updates.description !== undefined) {
        const description = updates.description.trim();
        if (description.length > BUSINESS_DESCRIPTION_MAX_LENGTH) {
          return { success: false, error: `Keep the description under ${BUSINESS_DESCRIPTION_MAX_LENGTH} characters.` };
        }
        patch.description = description || null;
      }
      if (updates.portfolioImages !== undefined) {
        const images = updates.portfolioImages.filter(Boolean);
        if (images.length !== PORTFOLIO_IMAGE_COUNT) {
          return { success: false, error: `A profile shows exactly ${PORTFOLIO_IMAGE_COUNT} portfolio images.` };
        }
        patch.portfolioImages = images;
      }
      if (updates.showOnPersonalProfile !== undefined) {
        patch.showOnPersonalProfile = !!updates.showOnPersonalProfile;
      }

      await updateDoc(doc(db, 'business_profiles', profileId), patch);
      return { success: true };
    } catch (e: any) {
      console.error('[BusinessService] updateProfile failed:', e);
      return { success: false, error: 'Could not save those changes.' };
    }
  },

  /**
   * A name or shop-link change goes back through review.
   *
   * The profile stays live and keeps serving its current name and link while
   * this is pending: a re-review is not a takedown. The request lands in the
   * same Legion Remote queue as new registrations rather than a second surface.
   */
  async requestNameOrLinkEdit(
    profileId: string,
    edit: { businessName?: string; shopLink?: string },
  ): Promise<Result> {
    const pending: PendingBusinessEdit = { requestedAt: serverTimestamp() };

    if (edit.businessName !== undefined) {
      const name = edit.businessName.trim();
      if (name.length < 2) return { success: false, error: 'Give your business a name.' };
      if (name.length > BUSINESS_NAME_MAX_LENGTH) {
        return { success: false, error: `Keep the name under ${BUSINESS_NAME_MAX_LENGTH} characters.` };
      }
      pending.businessName = name;
    }
    if (edit.shopLink !== undefined) {
      if (!isValidShopLink(edit.shopLink)) return { success: false, error: 'Add a working link to your shop.' };
      pending.shopLink = normalizeShopLink(edit.shopLink);
    }
    if (!pending.businessName && !pending.shopLink) {
      return { success: false, error: 'Nothing changed.' };
    }

    try {
      const db = await getDbSafe();
      // hasPendingEdit is a plain boolean rather than a `pendingEdit != null`
      // query: Firestore's != treats "absent" and "null" differently, so the
      // queue would silently miss rows depending on how the field was written.
      await updateDoc(doc(db, 'business_profiles', profileId), {
        pendingEdit: pending,
        hasPendingEdit: true,
      });
      return { success: true };
    } catch (e: any) {
      console.error('[BusinessService] requestNameOrLinkEdit failed:', e);
      return { success: false, error: 'Could not submit that change for review.' };
    }
  },

  /** Profiles carrying an unreviewed name/link change, for the same admin queue. */
  async fetchPendingEdits(): Promise<BusinessProfile[]> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'business_profiles'),
        where('hasPendingEdit', '==', true),
        limit(100),
      ));
      return snap.docs.map(d => mapDoc<BusinessProfile>(d)).filter(p => !!p.pendingEdit);
    } catch (e) {
      console.error('[BusinessService] fetchPendingEdits failed:', e);
      return [];
    }
  },

  async resolvePendingEdit(profileId: string, reviewerId: string, approve: boolean, reason?: string): Promise<Result> {
    try {
      const db = await getDbSafe();
      const profileRef = doc(db, 'business_profiles', profileId);
      const snap = await getDoc(profileRef);
      if (!snap.exists()) return { success: false, error: 'That profile no longer exists.' };
      const profile = mapDoc<BusinessProfile>(snap);
      const pending = profile.pendingEdit;
      if (!pending) return { success: false, error: 'There is no pending change on that profile.' };

      const patch: any = { pendingEdit: null, hasPendingEdit: false, lastEditedAt: serverTimestamp() };
      if (approve) {
        if (pending.businessName) {
          patch.businessName = pending.businessName;
          patch.businessNameLower = pending.businessName.toLowerCase();
        }
        if (pending.shopLink) patch.shopLink = pending.shopLink;
      }
      await updateDoc(profileRef, patch);

      await BusinessService._notify(
        profile.ownerId,
        approve ? BUSINESS_NOTIFICATION_TYPES.EDIT_APPROVED : BUSINESS_NOTIFICATION_TYPES.EDIT_DENIED,
        approve ? 'Business change approved' : 'Business change declined',
        approve
          ? `Your update to ${profile.businessName} is live.`
          : (reason || 'Your requested change was not approved.'),
        { businessProfileId: profileId },
      );

      await BusinessService._audit(
        reviewerId, profile.ownerId,
        approve ? 'business_edit_approved' : 'business_edit_denied',
        reason || (approve ? 'Business name/link change approved' : 'Business name/link change denied'),
        { businessProfileId: profileId, pendingEdit: { ...pending, requestedAt: undefined } },
      );

      return { success: true };
    } catch (e: any) {
      console.error('[BusinessService] resolvePendingEdit failed:', e);
      return { success: false, error: 'Could not resolve that change.' };
    }
  },

  // ── Media ──────────────────────────────────────────────────────────────────

  /**
   * Portfolio, feed and product images all land under users/{ownerId}/business/,
   * which storage.rules already covers: public read, owner-only write. Public
   * read is required — the /business/:id link is viewable logged out — and the
   * bucket's CORS policy already lists the app's origins (storage_cors.cjs), so
   * a future canvas export of a business card would work the way Yap downloads do.
   */
  async uploadBusinessMedia(
    ownerId: string,
    file: File | Blob,
    kind: 'portfolio' | 'feed' | 'product',
  ): Promise<string> {
    const storage = await getStorageSafe();
    const name = (file as File).name || '';
    const ext = (name.split('.').pop() || '').toLowerCase();
    const safeExt = /^[a-z0-9]{1,5}$/.test(ext) ? ext : (file.type?.startsWith('video/') ? 'mp4' : 'jpg');
    const path = `users/${ownerId}/business/${kind}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${safeExt}`;
    const snapshot = await uploadBytes(ref(storage, path), file, {
      contentType: file.type || (safeExt === 'mp4' ? 'video/mp4' : 'image/jpeg'),
    });
    return await getDownloadURL(snapshot.ref);
  },

  // ───────────────────────────────────────────────────────────────────────────
  // §5 FEED
  // ───────────────────────────────────────────────────────────────────────────

  async createFeedPost(
    profileId: string,
    authorId: string,
    post: { text: string; mediaUrls?: string[]; mediaType?: 'image' | 'video' | 'text' },
  ): Promise<Result> {
    const text = (post.text || '').trim();
    const mediaUrls = (post.mediaUrls || []).filter(Boolean);
    if (!text && mediaUrls.length === 0) return { success: false, error: 'Write something or add an image.' };
    if (text.length > FEED_POST_MAX_LENGTH) {
      return { success: false, error: `Keep posts under ${FEED_POST_MAX_LENGTH} characters.` };
    }

    try {
      const db = await getDbSafe();
      _checkRateLimit(authorId, `businessFeed_${profileId}`, 5000);
      await addDoc(collection(db, 'business_profiles', profileId, 'feed'), {
        authorId,
        text,
        mediaUrls,
        mediaType: post.mediaType || (mediaUrls.length ? 'image' : 'text'),
        createdAt: serverTimestamp(),
      });
      return { success: true };
    } catch (e: any) {
      console.error('[BusinessService] createFeedPost failed:', e);
      return { success: false, error: e?.message?.startsWith('Please wait') ? e.message : 'Could not post that.' };
    }
  },

  async getFeed(profileId: string, max = 50): Promise<BusinessFeedPost[]> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'business_profiles', profileId, 'feed'),
        orderBy('createdAt', 'desc'),
        limit(max),
      ));
      return snap.docs.map(d => mapDoc<BusinessFeedPost>(d));
    } catch (e) {
      console.error('[BusinessService] getFeed failed:', e);
      return [];
    }
  },

  async deleteFeedPost(profileId: string, postId: string): Promise<Result> {
    try {
      const db = await getDbSafe();
      await deleteDoc(doc(db, 'business_profiles', profileId, 'feed', postId));
      return { success: true };
    } catch (e) {
      console.error('[BusinessService] deleteFeedPost failed:', e);
      return { success: false, error: 'Could not delete that post.' };
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // §6 STORE
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Add a product, capped at STORE_ITEM_CAP.
   *
   * Same shape as GovernanceService's "1 hosted room" / "5 owned forums" caps: a
   * transaction reads the counter on the parent, refuses at the cap, and writes
   * the child and the bumped counter together. Two concurrent adds contend on
   * the parent read, so one retries rather than both squeezing past 15.
   *
   * The counter is denormalized precisely so the security rule can check it —
   * a rule cannot count a subcollection, so a rule reading a live count is not
   * an option and the cap would otherwise be client-side only.
   */
  async addStoreItem(
    profileId: string,
    item: { title: string; description: string; price: string; imageUrl: string; purchaseLink: string },
  ): Promise<Result<{ itemId: string }>> {
    const title = (item.title || '').trim();
    if (!title) return { success: false, error: 'Give the product a title.' };
    if (!isValidShopLink(item.purchaseLink)) return { success: false, error: 'Add a working purchase link.' };
    if (!item.imageUrl) return { success: false, error: 'Add a product image.' };

    try {
      const db = await getDbSafe();
      const profileRef = doc(db, 'business_profiles', profileId);
      const itemRef = doc(collection(db, 'business_profiles', profileId, 'store'));

      await runTransaction(db, async (tx) => {
        const profileSnap = await tx.get(profileRef);
        if (!profileSnap.exists()) throw new Error('That business profile no longer exists.');
        const count = profileSnap.data().storeItemCount || 0;
        if (count >= STORE_ITEM_CAP) {
          throw new Error(`Your store is full at ${STORE_ITEM_CAP} products. Remove one to add another.`);
        }
        tx.set(itemRef, {
          title,
          description: (item.description || '').trim(),
          price: (item.price || '').trim(),
          imageUrl: item.imageUrl,
          purchaseLink: normalizeShopLink(item.purchaseLink),
          createdAt: serverTimestamp(),
        });
        tx.update(profileRef, { storeItemCount: count + 1, lastEditedAt: serverTimestamp() });
      });

      return { success: true, data: { itemId: itemRef.id } };
    } catch (e: any) {
      console.error('[BusinessService] addStoreItem failed:', e);
      return { success: false, error: e?.message || 'Could not add that product.' };
    }
  },

  async updateStoreItem(profileId: string, itemId: string, updates: Partial<BusinessStoreItem>): Promise<Result> {
    try {
      const db = await getDbSafe();
      const patch: any = {};
      if (updates.title !== undefined) {
        const title = updates.title.trim();
        if (!title) return { success: false, error: 'Give the product a title.' };
        patch.title = title;
      }
      if (updates.description !== undefined) patch.description = updates.description.trim();
      if (updates.price !== undefined) patch.price = updates.price.trim();
      if (updates.imageUrl !== undefined) patch.imageUrl = updates.imageUrl;
      if (updates.purchaseLink !== undefined) {
        if (!isValidShopLink(updates.purchaseLink)) return { success: false, error: 'Add a working purchase link.' };
        patch.purchaseLink = normalizeShopLink(updates.purchaseLink);
      }
      await updateDoc(doc(db, 'business_profiles', profileId, 'store', itemId), patch);
      return { success: true };
    } catch (e) {
      console.error('[BusinessService] updateStoreItem failed:', e);
      return { success: false, error: 'Could not save that product.' };
    }
  },

  /** Removing decrements the same counter the cap is measured against. */
  async deleteStoreItem(profileId: string, itemId: string): Promise<Result> {
    try {
      const db = await getDbSafe();
      const profileRef = doc(db, 'business_profiles', profileId);
      const itemRef = doc(db, 'business_profiles', profileId, 'store', itemId);

      await runTransaction(db, async (tx) => {
        const profileSnap = await tx.get(profileRef);
        const itemSnap = await tx.get(itemRef);
        if (!itemSnap.exists()) return; // already gone; leave the counter alone
        tx.delete(itemRef);
        const count = profileSnap.exists() ? (profileSnap.data().storeItemCount || 0) : 0;
        tx.update(profileRef, {
          storeItemCount: Math.max(0, count - 1),
          lastEditedAt: serverTimestamp(),
        });
      });

      return { success: true };
    } catch (e) {
      console.error('[BusinessService] deleteStoreItem failed:', e);
      return { success: false, error: 'Could not remove that product.' };
    }
  },

  async getStore(profileId: string): Promise<BusinessStoreItem[]> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'business_profiles', profileId, 'store'),
        orderBy('createdAt', 'desc'),
        limit(STORE_ITEM_CAP),
      ));
      return snap.docs.map(d => mapDoc<BusinessStoreItem>(d));
    } catch (e) {
      console.error('[BusinessService] getStore failed:', e);
      return [];
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // §7 PURCHASE TRACKING
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Record the tap and return the URL to open.
   *
   * Logged-out visitors never reach this — they get the raw purchaseLink and no
   * tracking, per the public-profile decision. That is enforced by the caller
   * AND by the create rule, which requires request.auth.
   */
  async recordStoreTap(
    userId: string,
    profile: BusinessProfile,
    item: BusinessStoreItem,
  ): Promise<Result<{ url: string; checkId: string }>> {
    try {
      const db = await getDbSafe();
      const trackingCode = generateTrackingCode();

      const created = await addDoc(collection(db, 'purchase_checks'), {
        userId,
        businessProfileId: profile.id,
        businessOwnerId: profile.ownerId,
        productId: item.id,
        productTitle: item.title,
        trackingCode,
        tappedAt: serverTimestamp(),
        status: 'pending',
        respondedAt: null,
        notifiedAt: null,
      });

      // Best-effort tap counter. A failed increment must not stop the user
      // reaching the shop — the tap is the point, the number is decoration.
      try {
        await updateDoc(doc(db, 'business_profiles', profile.id), { storeTapCount: increment(1) });
      } catch (e) {
        console.warn('[BusinessService] storeTapCount increment failed:', e);
      }

      return {
        success: true,
        data: { url: appendTrackingCode(item.purchaseLink, trackingCode), checkId: created.id },
      };
    } catch (e) {
      console.error('[BusinessService] recordStoreTap failed:', e);
      // Still hand back a usable URL: failing to track is not a reason to fail
      // to send the customer to the shop.
      return { success: true, data: { url: normalizeShopLink(item.purchaseLink), checkId: '' } };
    }
  },

  /**
   * The most recent unanswered tap for this user.
   *
   * No expiry window, deliberately: someone can tap out, buy three days later,
   * and still be asked on their next return. The arming flag in
   * usePurchaseReturn is what keeps this from firing on every unrelated resume.
   */
  async getPendingCheck(userId: string): Promise<PurchaseCheck | null> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'purchase_checks'),
        where('userId', '==', userId),
        where('status', '==', 'pending'),
        orderBy('tappedAt', 'desc'),
        limit(1),
      ));
      return snap.empty ? null : mapDoc<PurchaseCheck>(snap.docs[0]);
    } catch (e) {
      console.warn('[BusinessService] getPendingCheck failed:', e);
      return null;
    }
  },

  /**
   * Answer the "did you complete your purchase?" prompt.
   *
   * A confirm only writes status here. The owner notification, its 24h rate
   * limit and confirmedPurchaseCount are all handled by the
   * onPurchaseCheckConfirmed trigger — a client that could send those directly
   * could also forge them.
   */
  async respondToCheck(checkId: string, confirmed: boolean): Promise<Result> {
    try {
      const db = await getDbSafe();
      await updateDoc(doc(db, 'purchase_checks', checkId), {
        status: confirmed ? 'confirmed' : 'declined',
        respondedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (e) {
      console.error('[BusinessService] respondToCheck failed:', e);
      return { success: false, error: 'Could not save your answer.' };
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // §8 SUSPENSION & DELETION
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Admin suspend. Flips status and nothing else — the rule's admin branch
   * permits exactly that key, the same way the Rooms force-end branch does.
   * This takes down ONE business profile: the owner's personal account and their
   * other business profiles are untouched.
   */
  async suspendProfile(profileId: string, adminId: string, reason: string): Promise<Result> {
    const suspendReason = (reason || '').trim();
    if (!suspendReason) return { success: false, error: 'Give a reason for the suspension.' };
    try {
      const db = await getDbSafe();
      const profileRef = doc(db, 'business_profiles', profileId);
      const snap = await getDoc(profileRef);
      if (!snap.exists()) return { success: false, error: 'That profile no longer exists.' };
      const profile = mapDoc<BusinessProfile>(snap);

      await updateDoc(profileRef, {
        status: 'suspended',
        suspendedAt: serverTimestamp(),
        suspendedBy: adminId,
        suspendReason,
      });

      await BusinessService._notify(
        profile.ownerId,
        BUSINESS_NOTIFICATION_TYPES.SUSPENDED,
        'Business profile suspended',
        `${profile.businessName} has been suspended. Reason: ${suspendReason}`,
        { businessProfileId: profileId },
      );

      await BusinessService._audit(adminId, profile.ownerId, 'business_profile_suspended',
        suspendReason, { businessProfileId: profileId, businessName: profile.businessName });

      return { success: true };
    } catch (e) {
      console.error('[BusinessService] suspendProfile failed:', e);
      return { success: false, error: 'Could not suspend that profile.' };
    }
  },

  async unsuspendProfile(profileId: string, adminId: string, reason?: string): Promise<Result> {
    try {
      const db = await getDbSafe();
      const profileRef = doc(db, 'business_profiles', profileId);
      const snap = await getDoc(profileRef);
      if (!snap.exists()) return { success: false, error: 'That profile no longer exists.' };
      const profile = mapDoc<BusinessProfile>(snap);

      await updateDoc(profileRef, {
        status: 'active',
        suspendedAt: null,
        suspendedBy: null,
        suspendReason: null,
      });

      await BusinessService._audit(adminId, profile.ownerId, 'business_profile_unsuspended',
        reason || 'Suspension lifted', { businessProfileId: profileId });

      return { success: true };
    } catch (e) {
      console.error('[BusinessService] unsuspendProfile failed:', e);
      return { success: false, error: 'Could not restore that profile.' };
    }
  },

  /**
   * Owner deletes their own profile.
   *
   * Deleting the parent leaves the feed and store subcollections orphaned —
   * Firestore has no cascading delete — so an onDocumentDeleted trigger does the
   * recursiveDelete, the same way Rooms' expiry cleanup does. That same trigger
   * writes the cooldown, because a cooldown the deleter can skip by not writing
   * it is no cooldown at all.
   */
  async deleteMyProfile(profileId: string, ownerId: string): Promise<Result> {
    try {
      const db = await getDbSafe();
      const snap = await getDoc(doc(db, 'business_profiles', profileId));
      if (!snap.exists()) return { success: false, error: 'That profile no longer exists.' };
      if (snap.data().ownerId !== ownerId) return { success: false, error: 'That profile is not yours.' };

      await deleteDoc(doc(db, 'business_profiles', profileId));
      return { success: true };
    } catch (e) {
      console.error('[BusinessService] deleteMyProfile failed:', e);
      return { success: false, error: 'Could not delete that profile.' };
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // §9 DISCOVERY
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * The Explore deck: active profiles in a fresh random order every load.
   *
   * Shuffled client-side rather than stored, per spec. Firestore cannot order
   * randomly, so this pulls a page and shuffles it — fine at this scale, and the
   * honest version of "random" rather than a stored order pretending to be one.
   */
  async getExploreDeck(max = 20): Promise<BusinessProfile[]> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(
        collection(db, 'business_profiles'),
        where('status', '==', 'active'),
        limit(100),
      ));
      const profiles = snap.docs.map(d => mapDoc<BusinessProfile>(d));
      // Fisher-Yates. Sorting by Math.random() - 0.5 is not a uniform shuffle
      // and visibly favours the original order at small N, which is exactly the
      // size this deck runs at.
      for (let i = profiles.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [profiles[i], profiles[j]] = [profiles[j], profiles[i]];
      }
      return profiles.slice(0, max);
    } catch (e) {
      console.error('[BusinessService] getExploreDeck failed:', e);
      return [];
    }
  },

  /**
   * Every business profile, suspended ones included, for the moderator queue.
   *
   * getExploreDeck cannot serve this: it filters to active, so a suspended
   * profile would be invisible to the very screen that has to restore it. The
   * list rule permits the unfiltered read only for an admin.
   */
  async fetchAllProfilesForAdmin(max = 100): Promise<BusinessProfile[]> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(collection(db, 'business_profiles'), limit(max)));
      return snap.docs
        .map(d => mapDoc<BusinessProfile>(d))
        // Suspended first: those are the ones a moderator is most likely here
        // to act on, and they are the only ones with a Restore button.
        .sort((a, b) => {
          if (a.status !== b.status) return a.status === 'suspended' ? -1 : 1;
          return (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0);
        });
    } catch (e) {
      console.error('[BusinessService] fetchAllProfilesForAdmin failed:', e);
      return [];
    }
  },

  /**
   * Search by business name, prefix-matched.
   *
   * Businesses have no usernames, so an exact-only match would dead-end on every
   * typo. Firestore has no full-text search (same limitation SearchService.
   * searchUsers works around), so this is a range query on the lowercased name
   * for the prefix, then a client-side contains pass over the same page so
   * "shea" also finds "Nubian Shea Co".
   */
  async searchBusinesses(searchTerm: string, max = 8): Promise<BusinessProfile[]> {
    const q = (searchTerm || '').trim().toLowerCase();
    if (!q) return [];
    try {
      const db = await getDbSafe();

      //  is the highest code point Firestore will sort against, so
      // [q, q+] is every string starting with q.
      const prefixSnap = await getDocs(query(
        collection(db, 'business_profiles'),
        where('status', '==', 'active'),
        orderBy('businessNameLower'),
        where('businessNameLower', '>=', q),
        where('businessNameLower', '<=', q + ''),
        limit(max),
      ));
      const byPrefix = prefixSnap.docs.map(d => mapDoc<BusinessProfile>(d));
      if (byPrefix.length >= max) return byPrefix;

      // Mid-string matches, which the range query cannot see.
      const scanSnap = await getDocs(query(
        collection(db, 'business_profiles'),
        where('status', '==', 'active'),
        limit(200),
      ));
      const seen = new Set(byPrefix.map(p => p.id));
      const contains = scanSnap.docs
        .map(d => mapDoc<BusinessProfile>(d))
        .filter(p => !seen.has(p.id) && (p.businessNameLower || p.businessName.toLowerCase()).includes(q));

      return [...byPrefix, ...contains].slice(0, max);
    } catch (e) {
      console.error('[BusinessService] searchBusinesses failed:', e);
      return [];
    }
  },

  /**
   * A profile view. Fire-and-forget, and counted for logged-out visitors too —
   * the public link is a real audience.
   */
  async recordProfileView(profileId: string): Promise<void> {
    try {
      const db = await getDbSafe();
      await updateDoc(doc(db, 'business_profiles', profileId), { viewCount: increment(1) });
    } catch {
      // Rules deny this to a logged-out visitor on any path but the narrow
      // view-counter branch; either way a lost view is not worth surfacing.
    }
  },

  // ── Internals ──────────────────────────────────────────────────────────────

  /** Non-critical: a business action must not fail because its notification did. */
  async _notify(userId: string, type: string, title: string, message: string, metadata: Record<string, any>) {
    try {
      const { NotificationService } = await import('./api');
      await NotificationService.sendNotification(userId, type, title, message, metadata);
    } catch (e) {
      console.warn('[BusinessService] notification failed (non-critical):', e);
    }
  },

  /** Every admin action here lands in moderation_logs, same as the Watcher panel's. */
  async _audit(actorId: string, targetId: string, actionType: string, reason: string, metadata: Record<string, any>) {
    try {
      const db = await getDbSafe();
      const { logAuditAction } = await import('./WatcherService');
      const actor = await getDoc(doc(db, 'users', actorId));
      await logAuditAction({
        watcher_id: actorId,
        watcher_username: actor.exists() ? (actor.data().username || 'Unknown') : 'Unknown',
        target_user_id: targetId,
        action_type: actionType,
        reason,
        timestamp: serverTimestamp(),
        metadata,
      } as any);
    } catch (e) {
      console.warn('[BusinessService] audit log write failed (non-critical):', e);
    }
  },
};
